import { useId } from "react";

interface SliderFieldProps {
  label: string;
  helpText?: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unitLabel: string;
  onChange: (value: number) => void;
}

/**
 * A slider paired with a numeric input showing the same value, so a
 * precise number is one tap away without the slider being the only way
 * to answer (a screen-reader or motor-impaired user can type the number
 * directly into the associated field instead).
 */
export function SliderField({ label, helpText, value, min, max, step = 5, unitLabel, onChange }: SliderFieldProps) {
  const sliderId = useId();
  const helpId = useId();

  return (
    <div className="slider-field">
      <div className="slider-field__header">
        <label htmlFor={sliderId} className="slider-field__label">
          {label}
        </label>
        <div className="slider-field__value-group">
          <input
            className="slider-field__number"
            type="number"
            inputMode="numeric"
            min={min}
            max={max}
            value={value}
            aria-label={`${label}, exact value in ${unitLabel}`}
            onChange={(e) => {
              const next = Number(e.target.value);
              if (!Number.isNaN(next)) onChange(Math.min(max, Math.max(min, next)));
            }}
          />
          <span className="slider-field__unit">{unitLabel}</span>
        </div>
      </div>
      <input
        id={sliderId}
        className="slider-field__range"
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        aria-describedby={helpText ? helpId : undefined}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <div className="slider-field__scale">
        <span>
          {min} {unitLabel}
        </span>
        <span>
          {max} {unitLabel}
        </span>
      </div>
      {helpText ? (
        <p id={helpId} className="slider-field__help">
          {helpText}
        </p>
      ) : null}
    </div>
  );
}
